import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams,useLocation } from "react-router";
import { CardBody, Card } from "reactstrap";
// components
import ALoader from "views/pages/manage/common/a-loader";
import ReceiptsList from "../components/ReceiptsList";
import BulkActionBar from "../components/BulkActionBar";
import ReceiptFiltersDrawer from "../components/ReceiptFiltersDrawer";
import DepositSlipForm from "../components/DepositSlipForm";
import DepositSuccessContent from "../components/DepositSuccessContent";
import StudentSearchBar from "../../components/StudentSearchBar";
import TopHeaderBar from "../../components/TopHeaderBar";
// hooks
import { useFetchList } from "../../hooks/useFetchList";
import { getReceiptListApi } from "../api/cashDeposits.service";
import { useFetchDropdown } from "../hooks/useDropdown";
import { updateRequest } from "../../../../../views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { useFetchBankAccount } from "../../hooks/useFetchBankAccount";
import { useSendForApproval } from "../hooks";
import { useModal } from "../../../../../contexts/customModal";
import { useNavigateToGivenPath } from "views/pages/manage/edpOps/studentDetails/paymentSummary/service";
// helpers
import { hasSameEffectiveDate } from "../../../utils";
import { failureToast } from "../../../../../views/pages/manage/common/utils/methods/toasterFunctions/function";
import { SAME_DAY_RECEIPT_ERROR_MGS } from "../constant";
import "../../styles/cashManagement.scss";
import { useBuTypeNameByBuId } from "../../hooks/useFetchBuType";
const SEARCH_STYLE = { fontSize: "12px" }; // Defined outside to prevent re-creation
const ViewReceiptsPage = () => {
  // 1. URL & Navigation
  const { id: businessAreaId, depositId, cashierOffice } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const depositSlipId = queryParams.get('depositSlipId');
  const navigate = useNavigateToGivenPath();

  // Memoize navigation to prevent recreation
  const goViewDeposit = useCallback(
    () => navigate(`/admin/cash-management/viewCashDeposit/${businessAreaId}`),
    [navigate, businessAreaId]
  );

  const goBack = ()=>{
    navigate('admin/paymentManagement');
  }

  // 2. Data Fetching
  const { bankDetails } = useFetchBankAccount(businessAreaId);
  const dropdownValues = useFetchDropdown();
  const { sendForApproval, loading: isSubmitting } = useSendForApproval();

  // 3. List Management
  const [searchInput, setSearchInput] = useState("");
  const [searchBy, setSearchBy] = useState("psid");
  const [state, setState] = useState();

  const {
    rows,
    loading: isListLoading,
    pagination,
    onPageChange,
    onPageSizeChange,
    filters,
    onFilterApply,
  } = useFetchList(getReceiptListApi, {
    initialPageSize: 100, // Consider lowering this
    businessAreaId,
    depositId,
    cashierOffice,
    initialFilters: {},
  }, setState);

  // 4. Selection Logic
  const [selectedIds, setSelectedIds] = useState(new Set());

  const updatedPagination = useMemo(
    () => ({
      ...pagination,
      page: pagination.page + 1, // Adjusting for 1-based UI
    }),
    [pagination]
  );

  // Safety: specific to current page data to prevent calculation errors
  const { selectedReceipts, selectedTotal } = useMemo(() => {
    if (!selectedIds.size) return { selectedReceipts: [], selectedTotal: 0 };

    // Only map items currently visible in rows to ensure data integrity
    const selectedItems = rows.filter((item) => selectedIds.has(item.id));
    const total = selectedItems.reduce(
      (acc, item) => acc + item.amountCollected,
      0
    );

    return { selectedReceipts: selectedItems, selectedTotal: total };
  }, [selectedIds, rows]);

  // Logic: Reset selection when page changes to prevent "Ghost selections"
  useEffect(() => {
    setSelectedIds(new Set());
  }, [pagination.page]);

    useEffect(() => {
    if (!depositId) return;

    (async () => {
  
      setSelectedIds(new Set(rows?.map(item => item.id) || []));
      const depositSlip = rows.length ? rows[0] : null;
       if(depositSlip)
      setDepositForm({
        viewer: {s3Key :depositSlip?.depositSlipS3Key, name:"Attached file"},
        depositSlipNo: depositSlip?.depositSlipNumber,
      });
    })();
  }, [depositId, businessAreaId, rows]);

  

  // 5. Modal & Form Logic
  const { showModal, hideModal } = useModal();
  const [depositForm, setDepositForm] = useState({
    viewer: null,
    depositSlipNo: null,
  });

  const onSaveDepositForm = useCallback(
    (form) => {
      setDepositForm(form);
      hideModal();
    },
    [hideModal]
  );

  const refreshReceiptList = useCallback(
    (response) => {
      onFilterApply({});
      setSelectedIds(new Set());
      setDepositForm({ viewer: null, depositSlipNo: null });

      showModal({
        title: `Cash deposit sent for approval`,
        content: (
          <DepositSuccessContent onClose={hideModal} details={response} onView={goViewDeposit} />
        ),
        maxWidth: "lg",
      });
    },
    [onFilterApply, showModal, goViewDeposit, hideModal]
  );

  const onSendForApproval = useCallback(() => {
    if (!hasSameEffectiveDate(selectedReceipts)) {
      failureToast(SAME_DAY_RECEIPT_ERROR_MGS);
      return;
    }
    if(!bankDetails?.description || !bankDetails?.bankAccount){
      failureToast("Bank details are missing. Please update bank details to proceed.");
      return;
    }
    sendForApproval(
      {
        depositForm,
        selectedReceipts,
        bankDetails,
        businessAreaId,
        depositSlipId,
        totalAmount: selectedTotal,
        cashierOffice,
      },
      refreshReceiptList,
      ()=>{},
      setState,
      state,
    );
  }, [
    selectedReceipts,
    depositForm,
    bankDetails,
    businessAreaId,
    selectedTotal,
    sendForApproval,
    refreshReceiptList,
    depositId,
  ]);

  // 6. Filter Handlers
  const applyFilterHandler = useCallback(
    (newFilter) => {
      onFilterApply(updateRequest(newFilter));
    },
    [onFilterApply]
  );

  const applyPrimaryFilter = useCallback(() => {
    onFilterApply({ [searchBy]: searchInput });
  }, [onFilterApply, searchBy, searchInput]);

  const openDepositSlipModal = useCallback(() => {
    showModal({
      title: `Upload bank deposit slip`,
      content: (
        <DepositSlipForm
          totalAmount={selectedTotal}
          onSave={onSaveDepositForm}
          depositForm={depositForm}
        />
      ),
      maxWidth: "lg",
    });
  }, [showModal, selectedTotal, onSaveDepositForm, depositForm]);

  const removeFilterFormHandler = useCallback(() => {
    onFilterApply({});
    setSelectedIds(new Set());
  }, [onFilterApply]);

    const { buTypeName, loading } = useBuTypeNameByBuId(businessAreaId)
  // 7. Render Helpers
  const headerData = useMemo(
    () => ({
      Type: buTypeName,
      "BU": businessAreaId,
      "Bank Name": bankDetails?.description || "-",
      "Account no.": bankDetails?.bankAccount || "-",
    }),
    [bankDetails, businessAreaId,buTypeName]
  );

  return (
    <>
      <div className="remove-container-top-space">
        <TopHeaderBar  title="Receipts" headerData={headerData} onBack={goBack} />

        <div className="cm-container">
          {(isSubmitting || isListLoading || loading) && <ALoader position="fixed" />}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between mb-3">
                <div className="heading-3 color-black semi-bold">
                  {pagination.total} Receipts
                </div>

                <div className="d-flex gap-md">
                  <div>
                    <StudentSearchBar
                      searchValue={searchInput}
                      onChange={setSearchInput}
                      onKeyDown={applyPrimaryFilter}
                      onSearchClick={applyPrimaryFilter}
                      dropdownValue={searchBy}
                      onDropdownChange={setSearchBy}
                      dropdownStyles={SEARCH_STYLE}
                    />
                  </div>

                  <ReceiptFiltersDrawer
                    applyFilterHandler={applyFilterHandler}
                    appliedForm={filters}
                    dropdownValues={dropdownValues}
                    onRemoveFilterHandler={removeFilterFormHandler}
                  />
                </div>
              </div>

              <ReceiptsList
                rows={rows}
                loading={isListLoading}
                pagination={updatedPagination}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      <BulkActionBar
        count={selectedIds.size}
        totalAmount={selectedTotal}
        onProceed={openDepositSlipModal}
        depositForm={depositForm}
        onViewDeposit={openDepositSlipModal}
        onSendForApproval={onSendForApproval}
        isApiCalling={isSubmitting}
      />
    </>
  );
};

export default ViewReceiptsPage;
